/**
 * 出金の確認（/confirm/withdrawal#<token>）。ダッシュボードの「出金」からも、MCP（Claude Code 等）の出金依頼からもここに来る。
 *
 * トークンの置き場所（SEC-ACCESS-23・2026-09-27 money/payout-security への追従）:
 *   - 画面の URL ではフラグメント（`#kwc_…`）に置く。フラグメントはブラウザがサーバーに送らない = ダッシュボードの静的配信・前段の
 *     プロキシのアクセスログに残らない
 *   - API には**本文**で送る（POST /v1/money/withdrawals/confirmation{,/options,/confirm}）。旧い `/withdrawals/confirm/:token` は呼ばない
 *   - 旧い形の URL（`/confirm/withdrawal/<token>`。2026-09-27 時点のサーバーの confirmUrl はまだこの形）で来たら、表示の前に
 *     `#token` の形へ置き換える（履歴にパスの形を残さない）。最初の 1 回の読み込みのログにだけはパスで残る = サーバーの confirmUrl を
 *     `#` の形に変えるまでの残り（報告済み）
 *
 * 守り（サーバーが強制・この画面はその結果を分かりやすく見せるだけ）:
 *   - ログイン中の本人の依頼でなければ 404（他人の確認 URL を踏ませても何も見えない）→「見つかりません」
 *   - パスキー（ユーザー検証あり）でしか確認できない。live は冷却の明けたパスキーだけ（403 passkey_cooldown に details.availableAt）
 *   - test モードの依頼だけ、production 以外のサーバーでは「パスキーなしで確認（テスト）」を許す（手元で流れを試す用）
 * MCP から来た依頼は目立つ注意を出す（AI が勝手に頼んだ出金を、人が気づかずに承認しないように）。
 *
 * 未ログインで開いた場合は App のルーティングがログイン画面へ送り、ログイン後にこの URL（#token 込み）へ戻す（state.from）。
 */
import { useCallback, useEffect, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { api, ApiError } from '../../api';
import { Empty, ErrorState, fullDate, PageHeader, Spinner } from '../../components/ui';
import { useResource } from '../../hooks';
import { errorText, useT } from '../../i18n';
import { money } from '../../money';
import type { AssertionOptionsJson, PasskeyInfo, WithdrawalToConfirm } from '../../moneyApi';
import { confirmPagePath, confirmTokenFromHash, isConfirmToken, liveConfirmReadiness } from '../../moneyLogic';
import { getPasskeyAssertion, isPasskeyCancel, passkeysSupported } from '../../passkey';
import { TopBar } from '../ProjectsPage';

type Outcome = 'transferring' | 'retry_later' | 'failed' | 'noop' | 'rejected';

const BASE = '/v1/money/withdrawals/confirmation';

/** 冷却中の 403 は「いつから確認できるか」を添える（サーバーの details.availableAt） */
function confirmErrorText(e: ApiError, t: ReturnType<typeof useT>['t']): string {
  const at = e.code === 'passkey_cooldown' && typeof e.details?.availableAt === 'string' ? e.details.availableAt : null;
  return at ? t('cf.cooldownUntil', { when: fullDate(at) }) : errorText(e);
}

export function ConfirmWithdrawalPage() {
  const { token: pathToken } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useT();
  // 旧い形（パスにトークン）→ #token の形に置き換える。置き換えるまでは何も読まない（API にも出さない）
  const legacy = pathToken && isConfirmToken(pathToken) ? pathToken : null;
  useEffect(() => {
    if (legacy) navigate(confirmPagePath(legacy), { replace: true });
  }, [legacy, navigate]);
  const token = legacy ? null : confirmTokenFromHash(location.hash);

  const r = useConfirmation(token);
  const keys = useResource<{ passkeys: PasskeyInfo[] }>(token ? '/v1/money/passkeys' : null);
  const [busy, setBusy] = useState<'passkey' | 'test' | 'cancel' | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [outcome, setOutcome] = useState<Outcome | 'cancelled' | null>(null);

  const w = r.data;
  const mode = w ? (w.livemode ? 'live' : 'test') : 'live';

  const confirm = async (how: 'passkey' | 'test') => {
    setBusy(how);
    setErr(null);
    try {
      let body: { assertion?: unknown; test?: boolean };
      if (how === 'passkey') {
        const { publicKey } = await api<{ publicKey: AssertionOptionsJson }>(`${BASE}/options`, { method: 'POST', body: { token } });
        body = { assertion: await getPasskeyAssertion(publicKey) };
      } else {
        body = { test: true };
      }
      const res = await api<{ outcome: Outcome }>(`${BASE}/confirm`, { method: 'POST', body: { token, ...body } });
      setOutcome(res.outcome);
    } catch (e) {
      if (isPasskeyCancel(e)) setErr(t('cf.passkeyClosed'));
      else setErr(e instanceof ApiError ? confirmErrorText(e, t) : String(e));
      r.reload();
    } finally {
      setBusy(null);
    }
  };

  const cancel = async () => {
    if (!w) return;
    setBusy('cancel');
    setErr(null);
    try {
      await api(`/v1/money/withdrawals/${w.id}/cancel`, { method: 'POST', body: {} });
      setOutcome('cancelled');
    } catch (e) {
      setErr(errorText(e as ApiError));
    } finally {
      setBusy(null);
    }
  };

  // トークンが無い・形が違う（#が欠けたコピー等）も「見つかりません」と同じ扱い（旧い形は置き換え中なので出さない）
  const notFound = (!legacy && !token) || (r.error && (r.error.status === 404 || r.error.status === 400));
  // live の依頼で、パスキーが全部冷却中なら「いつから確認できるか」を先に出す（押してから 403 を見せない）
  const readiness = keys.data ? liveConfirmReadiness(keys.data.passkeys, new Date()) : null;

  return (
    <div className="page-wrap">
      <TopBar />
      <main className="container narrow" id="main">
        <PageHeader eyebrow={t('cf.eyebrow')} title={t('cf.title')}>
          {w && <span className={`pill ${w.livemode ? 'pill-accent' : ''}`}>{t(`money.mode.${mode}`)}</span>}
        </PageHeader>
        {(legacy || (r.loading && !w && !outcome)) && <Spinner />}
        {notFound && (
          <section className="card">
            <Empty title={t('cf.notFoundTitle')} icon="lock">
              <p>{t('cf.notFoundBody')}</p>
            </Empty>
          </section>
        )}
        {r.error && !notFound && <ErrorState error={r.error} what={t('cf.title')} onRetry={r.reload} />}

        {outcome && <Result outcome={outcome} w={w} />}

        {w && !outcome && (
          <section className="card confirm-card">
            {w.requestedVia === 'mcp' && (
              <div className="alert alert-warn" role="note">
                <span>{t('cf.fromMcp')}</span>
              </div>
            )}
            {w.status === 'awaiting_confirmation' && <p className="small">{t('cf.lead')}</p>}

            <dl className="kv kv-compact confirm-kv">
              <div className="kv-row">
                <dt>{t('cf.amount')}</dt>
                <dd>{money(w.amount, w.currency)}</dd>
              </div>
              {w.fee && (
                <div className="kv-row muted">
                  <dt>{t('cf.fee')}</dt>
                  <dd>−{money(w.fee.total, w.currency)}</dd>
                </div>
              )}
              {w.net !== null && (
                <div className="kv-row quote-net">
                  <dt>{t('cf.net')}</dt>
                  <dd>{money(w.net, w.currency)}</dd>
                </div>
              )}
              <div className="kv-row">
                <dt>{t('cf.speed')}</dt>
                <dd>{t(`wd.${w.method}`)}</dd>
              </div>
              <div className="kv-row">
                <dt>{t('cf.expires')}</dt>
                <dd>{fullDate(w.expiresAt)}</dd>
              </div>
            </dl>

            {w.status !== 'awaiting_confirmation' && (
              <p className="alert alert-info" role="status">
                {t(`cf.status.${w.status}`)}
              </p>
            )}
            {err && (
              <p className="alert alert-error" role="alert">
                {err}
              </p>
            )}

            {w.status === 'awaiting_confirmation' && (
              <>
                {!w.livemode && <p className="muted small">{t('cf.testNote')}</p>}
                <div className="form-actions confirm-actions">
                  <button type="button" className="btn btn-ghost" onClick={() => void cancel()} disabled={busy !== null}>
                    {busy === 'cancel' ? t('common.working') : t('cf.cancel')}
                  </button>
                  {!w.livemode && (
                    <button type="button" className="btn btn-ghost" onClick={() => void confirm('test')} disabled={busy !== null}>
                      {busy === 'test' ? t('common.working') : t('cf.testConfirm')}
                    </button>
                  )}
                  <button type="button" className="btn btn-primary" onClick={() => void confirm('passkey')} disabled={busy !== null || !passkeysSupported()}>
                    {busy === 'passkey' ? t('common.working') : t('cf.confirm')}
                  </button>
                </div>
                {!passkeysSupported() && <p className="muted small">{t('pk.unsupported')}</p>}
                {w.livemode && readiness?.state === 'later' && (
                  <p className="alert alert-warn" role="note">
                    {t('cf.cooldownUntil', { when: fullDate(readiness.at.toISOString()) })}
                  </p>
                )}
                {w.livemode && readiness?.state === 'none' && <p className="alert alert-warn">{t('cf.noPasskey')}</p>}
                <p className="small">
                  <Link to="/money/passkeys">{t('money.nav.passkeys')} →</Link>
                </p>
              </>
            )}
          </section>
        )}
      </main>
    </div>
  );
}

/**
 * 確認する依頼の中身を読む（POST { token }）。useResource は GET 専用なので、同じ形（data / error / loading / reload）を小さく持つ。
 * WHY GET にしないか: トークンを URL（クエリ・パス）に載せない = サーバーのアクセスログに残さない（SEC-ACCESS-23）
 */
function useConfirmation(token: string | null) {
  const [state, setState] = useState<{ data: WithdrawalToConfirm | null; error: ApiError | null; loading: boolean }>({ data: null, error: null, loading: !!token });
  const [tick, setTick] = useState(0);
  useEffect(() => {
    if (!token) return;
    let alive = true;
    setState((s) => ({ ...s, loading: true }));
    api<WithdrawalToConfirm>(BASE, { method: 'POST', body: { token } })
      .then((data) => alive && setState({ data, error: null, loading: false }))
      .catch((e: unknown) => alive && setState({ data: null, error: e instanceof ApiError ? e : new ApiError(0, 'client_error', String(e)), loading: false }));
    return () => {
      alive = false;
    };
  }, [token, tick]);
  const reload = useCallback(() => setTick((n) => n + 1), []);
  return { ...state, reload };
}

function Result({ outcome, w }: { outcome: Outcome | 'cancelled'; w: WithdrawalToConfirm | null }) {
  const { t } = useT();
  const mode = w?.livemode === false ? 'test' : 'live';
  const net = w?.net !== null && w?.net !== undefined ? money(w.net, w.currency) : '';
  const view =
    outcome === 'transferring' || outcome === 'noop'
      ? { tone: 'ok', title: t('cf.sentTitle'), body: t('cf.sentBody', { net }) }
      : outcome === 'retry_later'
        ? { tone: 'info', title: t('cf.queuedTitle'), body: t('cf.queuedBody') }
        : outcome === 'cancelled'
          ? { tone: 'info', title: t('cf.cancelled'), body: '' }
          : outcome === 'rejected'
            ? { tone: 'error', title: t('cf.status.rejected'), body: '' }
            : { tone: 'error', title: t('cf.failedTitle'), body: t('cf.failedBody') };
  return (
    <section className="card">
      <div className={`alert alert-${view.tone}`} role="status">
        <strong>{view.title}</strong>
        {view.body && <span>{view.body}</span>}
      </div>
      <div className="form-actions">
        <Link to={`/money/${mode}/withdraw`} className="btn btn-ghost">
          {t('cf.newWithdrawal')}
        </Link>
        <Link to={`/money/${mode}/earnings`} className="btn btn-primary">
          {t('cf.toEarnings')}
        </Link>
      </div>
    </section>
  );
}
